import React, { memo, useContext, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import equal from 'fast-deep-equal';

import BadgeWithModal from '../BadgeWithModal';
import BadgeModalContent from '../BadgeModalContent';
import useInView from '../../../lib/hooks/useInView';
import { ANALYTICS_EVENTS } from '../../constants';
import { localyticsEvent } from '../../../lib/utils';
import PageContext from '../../context/page';
import StationContext from '../../context/station';
import performTracking from '../../../lib/utils/performTracking';

function BadgeAd({
  campaignId,
  imageUrl,
  title,
  description,
  callToAction,
  detailsImageUrl,
  targetUrl,
  impressionTrackingUrls,
  detailsImpressionTrackingUrls,
  clickTrackingUrl,
}) {
  const ref = useRef();
  const page = useContext(PageContext);
  const stationId = useContext(StationContext);

  const isVisible = useInView(ref, {
    threshold: 1,
    once: true,
    rootMargin: '0px 0px -15% 0px',
  }, [ref]);

  const handleModalOpen = useCallback(() => {
    performTracking(detailsImpressionTrackingUrls);
  }, [detailsImpressionTrackingUrls]);

  const handleClick = useCallback(() => {
    localyticsEvent(ANALYTICS_EVENTS.BADGE_AD_OPENED, {
      Context: page,
      UISource: 'Web',
      Station_ID: stationId.toString(),
      Ad_ID: campaignId.toString(),
    });
  }, [page, stationId, campaignId]);

  const handleCTAClick = useCallback(() => {
    localyticsEvent(ANALYTICS_EVENTS.BADGE_AD_CONTENT_CLICKED, {
      Context: page,
      UISource: 'Web',
      Station_ID: stationId.toString(),
      Ad_ID: campaignId.toString(),
    });
  }, [page, stationId, campaignId]);

  useEffect(() => {
    if (isVisible) {
      performTracking(impressionTrackingUrls);
    }
  }, [isVisible, impressionTrackingUrls]);

  return (
    <div ref={ref}>
      <BadgeWithModal
        imageUrl={imageUrl}
        title={title}
        onClick={handleClick}
        onModalOpen={handleModalOpen}
      >
        <BadgeModalContent
          imageUrl={imageUrl}
          title={title}
          description={description}
          detailsImageUrl={detailsImageUrl}
          callToAction={callToAction}
          targetUrl={targetUrl}
          clickTrackingUrl={clickTrackingUrl}
          onCTAClick={handleCTAClick}
        />
      </BadgeWithModal>
    </div>
  );
}

export default memo(BadgeAd, equal);

BadgeAd.propTypes = {
  campaignId: PropTypes.number.isRequired,
  imageUrl: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  callToAction: PropTypes.string,
  detailsImageUrl: PropTypes.string,
  targetUrl: PropTypes.string,
  clickTrackingUrl: PropTypes.string,
  impressionTrackingUrls: PropTypes.arrayOf(PropTypes.string),
  detailsImpressionTrackingUrls: PropTypes.arrayOf(PropTypes.string),
};

BadgeAd.defaultProps = {
  description: undefined,
  detailsImageUrl: undefined,
  callToAction: undefined,
  targetUrl: undefined,
  clickTrackingUrl: undefined,
  impressionTrackingUrls: [],
  detailsImpressionTrackingUrls: [],
};
